import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "../practitionerProfileForm/ppf.css";
import { ApiConnectionString } from "../../../constants/ApiConntection";

export default function ManageProfilePicture({ user, isEditing }) {
  const inputRef = useRef(null);

  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const setImageAndCleanup = (nextUrl) => {
    setImageUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return nextUrl;
    });
  };

  const fetchImageUrlIfExists = async () => {
    if (!user?.id) return;

    try {
      const response = await axios.get(
        `${ApiConnectionString}/api/image/profile/${user.id}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          responseType: "blob",
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 204,
        }
      );

      if (response.status === 204) {
        setImageAndCleanup("");
        return;
      }

      const objectUrl = URL.createObjectURL(response.data);
      setImageAndCleanup(objectUrl);
    } catch {
      setImageAndCleanup("");
    }
  };

  useEffect(() => {
    fetchImageUrlIfExists();

    return () => {
      setImageUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return "";
      });
    };
  }, [user?.id]);

  const upload = async (file) => {
    if (!file || !user?.token) return;

    setLoading(true);
    setMessage("");

    try {
      const formData = new FormData();
      formData.append("image", file);

      await axios.post(`${ApiConnectionString}/api/image/profile/`, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setMessage("Uploaded");
      await fetchImageUrlIfExists();
    } catch (err) {
      setMessage(err?.response?.data || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const remove = async () => {
    if (!user?.token) return;

    setLoading(true);
    setMessage("");

    try {
      await axios.delete(`${ApiConnectionString}/api/image/profile/`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });

      setMessage("Deleted");

      if (inputRef.current) inputRef.current.value = "";
      await fetchImageUrlIfExists();
    } catch (err) {
      setMessage(err?.response?.data || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pp-upload-card">
      {message ? (
        <div className="alert alert-info pp-upload-alert">{message}</div>
      ) : null}

      <div className="pp-upload-body">
        {isEditing ? (
          <div className="pp-upload-avatar-wrap">
            <div className="pp-upload-subtitle">
              This is how it appears on search
            </div>

            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Profile"
                className="pp-upload-avatar"
              />
            ) : (
              <div className="pp-upload-avatar pp-upload-avatar-empty">
                <span className="pp-upload-avatar-empty-text">No image</span>
              </div>
            )}
          </div>
        ) : null}

        {isEditing ? (
          <div className="pp-upload-actions">
            <input
              ref={inputRef}
              className="form-control bcs-brand-input pp-upload-file"
              type="file"
              accept="image/*"
              disabled={loading}
              onChange={async (e) => {
                const file = e.target.files?.[0] || null;
                if (!file) return;

                await upload(file);

                if (inputRef.current) inputRef.current.value = "";
              }}
            />

            <div className="pp-upload-btn-row">
              <button
                type="button"
                className="btn brand-purple-inline pp-upload-btn"
                disabled={loading}
                onClick={() => inputRef.current && inputRef.current.click()}
              >
                {loading ? "Working" : imageUrl ? "Change image" : "Choose image"}
              </button>

              {imageUrl ? (
                <button
                  type="button"
                  className="btn brand-purple-outline pp-upload-btn"
                  disabled={loading}
                  onClick={remove}
                >
                  Remove
                </button>
              ) : null}
            </div>

            <div className="pp-upload-hint">
              JPG or PNG works best. Square images look great.
            </div>
          </div>
        ) : null}
      </div>

      {imageUrl ? (
        <div className="pp-upload-preview">
          {isEditing ? (
            <div className="pp-upload-subtitle">
              This is how it displays on your main page
            </div>
          ) : null}

          <img
            src={imageUrl}
            alt="Profile preview"
            className="pp-upload-preview-img"
          />
        </div>
      ) : null}
    </div>
  );
}
